import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { GenericModal } from '@org/ui';
import {
  AnnouncementForm,
  type AnnouncementFormValues,
} from '../../app/components/forms/announcement';
import { trpc } from '@org/clients/school';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';

export const Route = createFileRoute('/announcements/new')({
  component: AnnouncementCreateRoute,
});

function AnnouncementCreateRoute() {
  const navigate = useNavigate({ from: '/announcements/new' });
  const utils = trpc.useUtils();
  const { t } = useTranslation('admin');

  const searchGroups = useCallback(
    async (query: string) => {
      if (query.length < 3) return [];
      const results = await utils.groups.searchActiveGroups.fetch({ search: query });
      return results.map((g) => ({
        value: g.groupId,
        label: g.description ?? g.groupId,
      }));
    },
    [utils],
  );

  const searchUsers = useCallback(
    async (query: string) => {
      if (query.length < 3) return [];
      const results = await utils.users.searchActiveUsers.fetch({ search: query });
      return results.map((u) => ({
        value: u.userId,
        label: `${u.firstName} ${u.lastName}`,
      }));
    },
    [utils],
  );

  const createMutation = trpc.announcements.create.useMutation({
    onSuccess: async () => {
      await utils.announcements.list.invalidate();
      toast.success(t('common.toasts.createSuccess', { entity: 'Announcement' }));
      handleClose();
    },
    onError: () => {
      toast.error(t('common.toasts.actionError', { action: 'create', entity: 'announcement' }));
    },
  });

  const handleSubmit = (values: AnnouncementFormValues) => {
    createMutation.mutate({
      title: values.title,
      body: values.body ?? undefined,
      mediaType: values.mediaType,
      mediaUrl: values.mediaUrl ?? undefined,
      posterUrl: values.posterUrl ?? undefined,
      ctaLabel: values.ctaLabel ?? undefined,
      ctaTo: values.ctaTo ?? undefined,
      targetType: values.targetType,
      status: values.status,
      skippable: values.skippable,
      startsAt: values.startsAt,
      endsAt: values.endsAt,
      priority: values.priority,
      style: values.style,
      groupIds: values.groupIds,
      userIds: values.userIds,
    });
  };

  const handleClose = () => {
    navigate({ to: '/announcements', replace: true });
  };

  return (
    <GenericModal
      show
      onClose={handleClose}
      title={t('announcements.form.createTitle')}
      size="xl"
      centered
      body={
        <AnnouncementForm
          onSubmit={handleSubmit}
          onClose={handleClose}
          isSubmitting={createMutation.isPending}
          onSearchGroups={searchGroups}
          onSearchUsers={searchUsers}
          labels={{
            title: t('announcements.form.title'),
            body: t('announcements.form.body'),
            mediaType: t('announcements.form.mediaType'),
            mediaUrl: t('announcements.form.mediaUrl'),
            posterUrl: t('announcements.form.posterUrl'),
            ctaLabel: t('announcements.form.ctaLabel'),
            ctaTo: t('announcements.form.ctaTo'),
            targetType: t('announcements.form.targetType'),
            status: t('announcements.form.status'),
            skippable: t('announcements.form.skippable'),
            startsAt: t('announcements.form.startsAt'),
            endsAt: t('announcements.form.endsAt'),
            priority: t('announcements.form.priority'),
            mediaNone: t('announcements.list.mediaLabels.none'),
            mediaImage: t('announcements.list.mediaLabels.image'),
            mediaVideo: t('announcements.list.mediaLabels.video'),
            mediaAnimation: t('announcements.list.mediaLabels.animation'),
            targetAll: t('announcements.form.targetAll'),
            targetE2e: t('announcements.form.targetE2e'),
            targetGroups: t('announcements.form.targetGroups'),
            targetUsers: t('announcements.form.targetUsers'),
            statusDraft: t('announcements.list.statusLabels.draft'),
            statusActive: t('announcements.list.statusLabels.active'),
            statusClosed: t('announcements.list.statusLabels.closed'),
            skippableOn: t('announcements.form.skippableOn'),
            skippableOff: t('announcements.form.skippableOff'),
            groups: t('announcements.form.groups'),
            users: t('announcements.form.users'),
            datePlaceholder: t('announcements.form.datePlaceholder'),
            style: t('announcements.form.style'),
          }}
        />
      }
      footer={<></>}
    />
  );
}
